import React, { useState, useEffect } from 'react';
import './ShopRatingPage.css';
import ShopRatingCard from '../components/ShopRatingCard';
import ShopsTable from '../components/ShopsTable';
import ShopRatingForm from '../components/ShopRatingForm';
import { useAuth } from '../context/AuthContext';

function ShopRatingPage() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedShop, setSelectedShop] = useState(null);
  const [sortBy, setSortBy] = useState('dlRating'); // dlRating, name, google, trustpilot
  const [searchQuery, setSearchQuery] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    fetchShops();
  }, []);

  const fetchShops = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/stores/ratings/all');
      if (!res.ok) throw new Error('Failed to fetch shops');
      const data = await res.json();
      setShops(data);
      setError('');
    } catch (err) {
      console.error('Error:', err);
      setError('Fehler beim Laden der Shops');
    } finally {
      setLoading(false);
    }
  };

  const handleRatingSubmitted = () => {
    // Aktualisiere die Bewertungen des Shops
    fetchShops().then(() => {
      // Finde den aktualisierten Shop
      const updated = shops.find(s => s._id === selectedShop?._id);
      if (updated) setSelectedShop(updated);
    });
  };

  const getSortedShops = () => {
    let sorted = [...shops];

    // Filter nach Suchquery
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      sorted = sorted.filter(s => 
        s.name.toLowerCase().includes(q) ||
        s.website?.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q)
      );
    }

    // Sortieren
    switch (sortBy) {
      case 'dlRating':
        sorted.sort((a, b) => {
          const aRating = a.dlCommunityRating?.average || 0;
          const bRating = b.dlCommunityRating?.average || 0;
          return bRating - aRating;
        });
        break;
      case 'name':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'trustpilot':
        sorted.sort((a, b) => {
          const aRating = a.externalRatings?.find(r => r.source === 'trustpilot')?.rating || 0;
          const bRating = b.externalRatings?.find(r => r.source === 'trustpilot')?.rating || 0;
          return bRating - aRating;
        });
        break;
      case 'trustedshops':
        sorted.sort((a, b) => {
          const aRating = a.externalRatings?.find(r => r.source === 'trustedshops')?.rating || 0;
          const bRating = b.externalRatings?.find(r => r.source === 'trustedshops')?.rating || 0;
          return bRating - aRating;
        });
        break;
      default:
        break;
    }

    return sorted;
  };

  const sortedShops = getSortedShops();

  if (loading) {
    return (
      <div className="shop-rating-page loading">
        <div className="loader">⏳ Shops werden geladen...</div>
      </div>
    );
  }

  return (
    <div className="shop-rating-page">
      <div className="page-header">
        <h1>🏪 Shop-Bewertungen</h1>
        <p className="subtitle">
          Entdecke alle Shops mit ihren Trustpilot-, Trusted Shops- und Community-Bewertungen.
          Teile deine Erfahrungen mit der Dichtlicht Community!
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="controls-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="🔍 Shops durchsuchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {selectedShop ? (
        <div className="detail-view">
          <button
            className="btn-back"
            onClick={() => setSelectedShop(null)}
          >
            ← Zurück
          </button>
          <ShopRatingCard
            shop={selectedShop}
            detailed={true}
            onRatingUpdated={handleRatingSubmitted}
          />
          {user && (
            <ShopRatingForm
              shopId={selectedShop._id}
              onSubmitted={handleRatingSubmitted}
            />
          )}
        </div>
      ) : (
        <ShopsTable onShopSelect={setSelectedShop} shops={getSortedShops()} />
      )}

      <div className="info-section">
        <h3>ℹ️ So funktioniert das?</h3>
        <ul>
          <li><strong>Trustpilot:</strong> Automatisch ausgelesen von Trustpilot</li>
          <li><strong>DL Community:</strong> Bewertungen von Dichtlicht.de Nutzern</li>
          <li><strong>Erfahrungsbericht:</strong> Schreib deine persönliche Erfahrung!</li>
        </ul>
      </div>
    </div>
  );
}

export default ShopRatingPage;
